import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Sparkles, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SEVERITY_STYLES, STATUS_STYLES } from "@/lib/incident";

const SEVERITIES = ["Critical", "High", "Medium", "Low"];
const STATUSES = ["New", "Reviewed", "Resolved"];

export const IncidentHeader = ({ draft, onSet, onTitleGenerated }) => {
  const sev = SEVERITY_STYLES[draft.severity] || SEVERITY_STYLES.Medium;
  const titleRef = useRef(null);
  const [naming, setNaming] = useState(false);

  const generateName = async () => {
    setNaming(true);
    try {
      const { data } = await api.post(`/incidents/${draft.id}/generate-title`);
      onTitleGenerated(data);
      toast.success("AI name applied");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate a name");
    } finally {
      setNaming(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <span className={`w-2.5 h-2.5 rounded-full ${sev.dot}`} />
        <Select value={draft.severity} onValueChange={(v) => onSet("severity", v)}>
          <SelectTrigger data-testid="severity-select" className={`w-36 h-8 rounded-sm bg-[#0A0A0A] border-border ${sev.text} font-bold text-xs uppercase tracking-widest`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-border rounded-sm">
            {SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={draft.status} onValueChange={(v) => onSet("status", v)}>
          <SelectTrigger data-testid="status-select" className={`w-32 h-8 rounded-sm bg-[#0A0A0A] border font-bold text-xs uppercase tracking-widest ${STATUS_STYLES[draft.status] || ""}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-border rounded-sm">
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" data-testid="edit-title-btn" title="Edit name"
            onClick={() => { titleRef.current?.focus(); titleRef.current?.select(); }}
            className="p-1.5 text-muted-foreground hover:text-white border border-transparent hover:border-border rounded-sm transition-colors duration-200">
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button type="button" data-testid="ai-name-btn" title="Create name with AI"
            onClick={generateName} disabled={naming}
            className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-[#007AFF] border border-[#007AFF]/40 hover:bg-[#007AFF]/10 rounded-sm transition-colors duration-200 disabled:opacity-60">
            {naming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {naming ? "Naming…" : "AI name"}
          </button>
        </div>
      </div>
      <Input ref={titleRef} value={draft.title} onChange={(e) => onSet("title", e.target.value)} data-testid="title-input"
        className="text-2xl sm:text-3xl font-heading font-black tracking-tighter bg-transparent border-0 border-b border-transparent hover:border-border focus-visible:border-[#007AFF] focus-visible:ring-0 px-0 h-auto rounded-none" />
    </div>
  );
};
